import { useCurrentUser, useUpdatePcfraStatus, useUserStore } from '~/client/hooks/storeHooks';
import { otherFieldDateUnsetValue } from '~/common/config/constants';
import { tempNumericId } from '~/common/tinybase/utils';
import { PcfraActivityType } from '~/common/types/pcfra';
import {
  RpeepStatus,
  PcfraPerson,
  PcfraStatus,
  UpdatePcfraSelfReferredPayload,
} from '~/common/types/people';
import {
  getPcfraActivityNoteText,
  getRpeepStatusNoteText,
  getPcfraStatusNoteText,
} from '~/common/utils/notes';

import { selfReferralActivityTypes } from './LogActivityDialog/helpers';

export type PcfraActivityParams = {
  propertyId: number;
  personId: number;
  note: string;
  /** Pass `null` to unset the current date */
  pcfraReviewDate?: string | null;
} & AnyUpdate;

export type PcfraStatusUpdate = {
  newPcfraStatus: PcfraStatus;
  shouldNotifyReferrer?: boolean;
};

export type RpeepStatusUpdate = {
  newRpeepStatus: RpeepStatus;
};

export type ActivityUpdate = {
  activityType: PcfraActivityType;
  activityTimestamp: string;
};

type AnyUpdate = PcfraStatusUpdate | RpeepStatusUpdate | ActivityUpdate;

const isPcfraStatusUpdate = (updateParams: AnyUpdate): updateParams is PcfraStatusUpdate =>
  'newPcfraStatus' in updateParams && !!updateParams.newPcfraStatus;

const isRpeepStatusUpdate = (updateParams: AnyUpdate): updateParams is RpeepStatusUpdate =>
  'newRpeepStatus' in updateParams && !!updateParams.newRpeepStatus;

export const getSelfReferralUpdate = (activityType: PcfraActivityType) =>
  selfReferralActivityTypes.includes(activityType)
    ? { isSelfReferred: activityType === 'self-referral-accepted' }
    : undefined;

export const useHandlePcfraActivity = () => {
  const user = useCurrentUser();

  const userStore = useUserStore();
  const updatePcfraStatus = useUpdatePcfraStatus();

  const handlePcfraActivity = ({
    propertyId,
    personId,
    note: userNote,
    pcfraReviewDate,
    ...updateParams
  }: PcfraActivityParams) => {
    if (!user || !userStore) {
      return;
    }
    const {
      pcfraStatus: prevPcfraStatus,
      pcfraReviewDate: prevReviewDate,
      rpeepStatus: prevRpeepStatus,
    } = <PcfraPerson | undefined>userStore.get('pcfraPeople', personId) || {};

    const hasUpdatedReviewDate = !!pcfraReviewDate && pcfraReviewDate !== prevReviewDate;
    const hasUnsetReviewDate = pcfraReviewDate === null && !!prevReviewDate;
    const hasReviewDateChange = hasUpdatedReviewDate || hasUnsetReviewDate;
    const newPcfraReviewDate = hasReviewDateChange ? pcfraReviewDate : undefined;

    let newPcfraStatus: PcfraStatus | undefined;
    let newRpeepStatus: RpeepStatus | undefined;
    let shouldNotifyReferrer: boolean | undefined;
    let selfReferralUpdate: Pick<UpdatePcfraSelfReferredPayload, 'isSelfReferred'> | undefined;
    let note: string;

    if (isPcfraStatusUpdate(updateParams)) {
      ({ newPcfraStatus, shouldNotifyReferrer } = updateParams);
      note = getPcfraStatusNoteText(user, userNote, {
        newPcfraStatus,
        prevPcfraStatus,
        newPcfraReviewDate,
      });
    } else if (isRpeepStatusUpdate(updateParams)) {
      ({ newRpeepStatus } = updateParams);
      note = getRpeepStatusNoteText(user, userNote, {
        newRpeepStatus,
        prevRpeepStatus,
      });
    } else {
      note = getPcfraActivityNoteText(user, userNote, { ...updateParams, newPcfraReviewDate });
      selfReferralUpdate = getSelfReferralUpdate(updateParams.activityType);
    }

    userStore.store.transaction(() => {
      if (selfReferralUpdate) {
        userStore.handleAndDispatchMessage('updatePcfraSelfReferred', personId, {
          propertyId,
          personId,
          ...selfReferralUpdate,
        });
      }

      if (hasReviewDateChange) {
        userStore.handleAndDispatchMessage('updatePcfraReviewDate', personId, {
          propertyId,
          personId,
          newDate: pcfraReviewDate || otherFieldDateUnsetValue,
        });
      }

      if (newPcfraStatus) {
        updatePcfraStatus({
          propertyId,
          personId,
          note,
          shouldNotifyReferrer,
          newStatus: newPcfraStatus,
        });
      }

      if (newRpeepStatus) {
        userStore.handleAndDispatchMessage('updateRpeepStatus', personId, {
          propertyId,
          personId,
          newStatus: newRpeepStatus,
        });
      }

      if (newRpeepStatus === RpeepStatus.Revoked) {
        userStore.handleAndDispatchMessage('revokeRpeep', personId, {
          propertyId,
          personId,
        });
      }

      userStore.handleAndDispatchMessage('createFireSafetyNote', personId, {
        noteId: String(tempNumericId()),
        propertyId,
        personId,
        note,
        createdBy: 'Home Visits',
        created: new Date().toISOString(),
      });
    });
  };

  return handlePcfraActivity;
};
